import { useCallback, useRef } from 'react';
import { Box, ButtonBase, Skeleton, Typography } from '@mui/material';
import { Img } from '@/components/blocks/MaterialImage';
import { Linkable } from '@/components/blocks/Linkable';
import { RepurchaseCoupon, useRepurchaseCoupon } from '@/data/Content/RepurchaseCoupon';
import { useCatalogEntrySearch } from '@/components/blocks/DpCatalogEntrySearch/useCatalogEntrySearch';
import { useRepurchaseExclusions } from '@/data/Content/RepurchaseExclusions';
import {
	formatCouponDate,
	formatCouponTime,
} from '@/components/blocks/DpRepurchaseCoupon/formatCouponDate';
import { ChevronRightIcon } from '@/components/blocks/DpRepurchaseCoupon/parts/CouponIcons';
import { RepurchaseCouponCard } from '@/components/blocks/DpRepurchaseCoupon/parts/RepurchaseCouponCard';
import { RepurchaseProductCard } from '@/components/blocks/DpRepurchaseCoupon/parts/RepurchaseProductCard';
import {
	repurchaseCarouselSX,
	repurchaseCouponSlotSX,
	repurchaseHeaderSX,
	repurchaseNextButtonSX,
	repurchaseRootSX,
	repurchaseSectionSX,
} from '@/components/blocks/DpRepurchaseCoupon/styles/container';
import {
	sponsoredHeroSX,
	sponsoredProductsSX,
	sponsoredRowSX,
	sponsoredSkeletonSX,
} from '@/components/blocks/DpCatalogEntrySearch/styles/container';
import {
	repurchaseLinkSX,
	repurchaseTermsSX,
	repurchaseTitleSX,
} from '@/components/blocks/DpRepurchaseCoupon/styles/typography';

export type RepurchaseCarouselContent = {
	title?: string;
	image?: string;
	video?: string;
	alt?: string;
	linkHref?: string;
	linkLabel?: string;
};

const TITLE_ID = 'dp-repurchase-title';
const FIRST_NAME_TOKEN = '{nombre}';
const DEFAULT_TITLE = `${FIRST_NAME_TOKEN}, elegimos esto para ti`;
const FIRST_NAME_TOKEN_PATTERN = /\{nombre\},?\s*/g;

const resolveTitle = (title: string, firstName?: string) =>
	firstName
		? title.replaceAll(FIRST_NAME_TOKEN, firstName)
		: title.replace(FIRST_NAME_TOKEN_PATTERN, '');

const buildTerms = (expirationDateTime: string | undefined, exclusions: string) =>
	[
		'Cupón personal y de un solo uso.',
		expirationDateTime
			? `Válido hasta el ${formatCouponDate(expirationDateTime, true)} a las ${formatCouponTime(
					expirationDateTime
			  )}.`
			: '',
		exclusions,
	]
		.filter(Boolean)
		.join(' ');

const CouponSlot = ({
	coupon,
	redeemable,
	onApply,
}: {
	coupon: RepurchaseCoupon;
	redeemable: boolean;
	onApply: () => void;
}) =>
	redeemable ? (
		<Box sx={repurchaseCouponSlotSX}>
			<RepurchaseCouponCard coupon={coupon} onApply={onApply} />
		</Box>
	) : null;

export const RepurchaseCarousel = ({ content }: { content: RepurchaseCarouselContent }) => {
	const rowRef = useRef<HTMLDivElement>(null);
	const { coupon, redeemable, applyCoupon } = useRepurchaseCoupon();
	const { skuList, products, loading } = useCatalogEntrySearch(coupon?.partNumbers ?? []);
	const { isExcluded } = useRepurchaseExclusions(products);

	const onNext = useCallback(() => {
		const row = rowRef.current;
		if (!row) return;
		const atEnd = row.scrollLeft + row.clientWidth >= row.scrollWidth - 1;
		row.scrollTo({ left: atEnd ? 0 : row.scrollLeft + row.clientWidth * 0.8 });
	}, []);

	if (!coupon || !coupon.partNumbers.length) {
		return null;
	}

	const { title = DEFAULT_TITLE, image, video, alt = '', linkHref, linkLabel } = content;

	return (
		<Box sx={repurchaseRootSX}>
			<Box component="section" aria-labelledby={TITLE_ID} sx={repurchaseSectionSX}>
				<Box component="header" sx={repurchaseHeaderSX}>
					<Typography id={TITLE_ID} component="h2" sx={repurchaseTitleSX}>
						{resolveTitle(title, coupon.firstName)}
					</Typography>
					{linkHref && linkLabel ? (
						<Linkable href={linkHref} sx={repurchaseLinkSX}>
							{linkLabel}
						</Linkable>
					) : null}
				</Box>
				<CouponSlot coupon={coupon} redeemable={redeemable} onApply={applyCoupon} />
				<Box sx={repurchaseCarouselSX}>
					<Box sx={sponsoredRowSX}>
						{video || image ? (
							<Box sx={sponsoredHeroSX}>
								{video ? (
									<video
										src={video}
										muted
										loop
										autoPlay
										playsInline
										preload="auto"
										aria-label={alt}
									/>
								) : (
									<Img src={image} alt={alt} />
								)}
							</Box>
						) : null}
						<Box ref={rowRef} sx={sponsoredProductsSX}>
							{loading
								? skuList.map((sku) => (
										<Skeleton
											key={`dp_repurchase_skeleton_${sku}`}
											variant="rectangular"
											height={260}
											sx={sponsoredSkeletonSX}
										/>
								  ))
								: products.map((product) => (
										<RepurchaseProductCard
											key={`dp_repurchase_${product.partNumber}`}
											product={product}
											excluded={isExcluded(product)}
										/>
								  ))}
						</Box>
					</Box>
					<ButtonBase aria-label="Ver más productos" onClick={onNext} sx={repurchaseNextButtonSX}>
						<ChevronRightIcon />
					</ButtonBase>
				</Box>
			</Box>
			<Typography component="p" sx={repurchaseTermsSX}>
				{buildTerms(coupon.expirationDateTime, coupon.exclusions)}
			</Typography>
		</Box>
	);
};
